/**
 * Container for a Settings component.
 */
import React from 'react'
import { connect } from 'react-redux'
import { Dispatch, AnyAction } from 'redux'
import { Manifest } from 'mytours-read-api-typescript'
import { RootStoreState } from '../../root/index'
import * as actions from '../actions'
import * as apiActions from '../../api/actions'
import * as navActions from '../../navigation/actions'
import { AppInfo, Language } from '../types'
import { AppMetaData } from '../../api/types'
import { ProcessingManifestState } from '../reducer'
import { getConfiguration } from '../../configuration'

export interface Props {
	appInfo: AppInfo
	appMetaData: AppMetaData
	appManifest?: Manifest
	loadingMetaData: boolean
	selectedLanguage?: Language
	locationEventsEnabled: boolean
	manifest?: ProcessingManifestState
	screenTimeout?: number
	keypadEnabled: boolean
}

export interface Actions {
	appInfoSelected: (id: string, token: string) => void
	updateAppMetaData: (id: string) => void
	updateAppManifest: () => void
	downloadManifest: () => void
	selectLanguage: (language: Language) => void
	selectScreenTimeout: (value?: number) => void
	enableLocationEvents: (value: boolean) => void
	navigateToPin: () => void
	navigateToQRScanner: () => void
	setInitialisedRentalMode: (value: boolean) => void
	lockAdminSettings: () => void
	cancelDownloadManifest: () => void
	finishDownloadManifest: () => void
	navigateToAppKeypadEntry: () => void
}

const mapStateToProps = (state: RootStoreState): Props => {
	const appMetaData = state.settings.appMetaData
	if (appMetaData === undefined) {
		throw Error('Must have meta data to get to this point')
	}
	return {
		appInfo: state.settings.appInfo,
		appManifest: state.settings.appManifest,
		appMetaData,
		loadingMetaData: state.settings.loadingMetaData,
		selectedLanguage: state.settings.selectedLanguage,
		locationEventsEnabled: state.settings.locationEventsEnabled,
		manifest: state.settings.processingManifest,
		screenTimeout: state.settings.screenTimeout,
		keypadEnabled: !!state.settings.appMetaData && state.settings.appMetaData.keypad_enabled === true,
	}
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): Actions => ({
	appInfoSelected: (id: string, token: string) => {
		// Don't need to worry about `projectId` since `appInfoSelected` is only used for native
		dispatch(actions.changeAppInfo({ id, projectId: '', token }))
	},
	updateAppMetaData: (projectId: string) => {
		const platform = getConfiguration().getPlatform()
		const deviceUUID = getConfiguration().getDeviceUUID()
		dispatch(apiActions.fetchAppMetaData.started({ projectId, devicePlatform: platform, deviceUdid: deviceUUID }))
	},
	updateAppManifest: () => {
		const platform = getConfiguration().getPlatform()
		const deviceUUID = getConfiguration().getDeviceUUID()
		dispatch(apiActions.fetchManifest.started({ devicePlatform: platform, deviceUdid: deviceUUID, isForSync: false }))
	},
	selectLanguage: (language: Language) => {
		dispatch(actions.appLanguageSelected(language))
	},
	selectScreenTimeout: (value?: number) => {
		dispatch(actions.screenTimeoutSelected(value))
	},
	enableLocationEvents: (value: boolean) => {
		dispatch(actions.enableLocationEvents(value))
	},
	navigateToPin: () => {
		dispatch(navActions.navigateToPin())
	},
	navigateToQRScanner: () => {
		dispatch(navActions.navigateToQRScanner())
	},
	setInitialisedRentalMode: (value: boolean) => {
		dispatch(actions.setInitialisedRentalMode(value))
	},
	lockAdminSettings: () => {
		dispatch(navActions.navigateToAdminSettings(false))
	},
	downloadManifest: () => {
		const platform = getConfiguration().getPlatform()
		const deviceUUID = getConfiguration().getDeviceUUID()
		dispatch(apiActions.fetchManifest.started({ devicePlatform: platform, deviceUdid: deviceUUID, isForSync: true }))
	},
	cancelDownloadManifest: () => {
		// might need to be smarter around canceling syncing content saga that is already in progress
		dispatch(actions.updateProcessingManifestState({ event: 'clear' }))
	},
	finishDownloadManifest: () => {
		dispatch(actions.updateProcessingManifestState({ event: 'clear' }))
	},
	navigateToAppKeypadEntry: () => {
		dispatch(navActions.navigateToAppKeypadEntry())
	},
})

export default (Component: React.ComponentType<Props & Actions>) =>
	connect(mapStateToProps, mapDispatchToProps)(Component)
